import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { startReminderScheduler, stopReminderScheduler } from "@/lib/reminders";
import { LayoutDashboard, Dumbbell, MessageSquareText, ListChecks, TrendingUp, LogOut, Flame, User, CalendarClock, Shield, MessageSquare } from "lucide-react";

const NAV_USER = [
  { to: "/", label: "COMANDO", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/program", label: "PROGRAMMA", icon: CalendarClock, testid: "nav-program" },
  { to: "/library", label: "LIBRERIA", icon: Dumbbell, testid: "nav-library" },
  { to: "/coach", label: "COACH IA", icon: MessageSquareText, testid: "nav-coach" },
  { to: "/workouts", label: "SCHEDE", icon: ListChecks, testid: "nav-workouts" },
  { to: "/progress", label: "PROGRESSI", icon: TrendingUp, testid: "nav-progress" },
];

// Admin è un coach, non un atleta — vede solo la Console (+ Libreria come riferimento esercizi)
const NAV_ADMIN = [
  { to: "/admin", label: "CONSOLE", icon: Shield, testid: "nav-admin" },
  { to: "/library", label: "LIBRERIA", icon: Dumbbell, testid: "nav-library" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const isAdmin = user?.role === "admin";
  const NAV = isAdmin ? NAV_ADMIN : NAV_USER;

  useEffect(() => {
    startReminderScheduler(() => user?.name || "atleta");
    return () => stopReminderScheduler();
  }, [user?.name]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0F]/95 backdrop-blur-md border-b border-[#2A2A35]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-[#FF3B30]" strokeWidth={2.5} />
              <span className="font-display font-black text-xl tracking-tight uppercase" data-testid="app-logo">
                Fit<span className="text-[#FF3B30]">Command</span>
              </span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `px-3 py-2 text-xs font-bold uppercase tracking-widest transition-colors duration-200 flex items-center gap-2 ${
                      isActive ? "text-[#FF3B30]" : "text-[#A0A0AB] hover:text-white"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" strokeWidth={2.5} />
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NavLink
              to="/messages"
              data-testid="nav-messages"
              className={({ isActive }) =>
                `relative p-2 border transition-all ${
                  isActive ? "border-[#E2FF31] text-[#E2FF31]" : "border-[#2A2A35] hover:border-white text-white"
                }`
              }
              aria-label="Messaggi"
              title="Messaggi"
            >
              <MessageSquare className="w-4 h-4" />
              {unread > 0 && (
                <span
                  data-testid="unread-badge"
                  className="absolute -top-1.5 -right-1.5 bg-[#FF3B30] text-white text-[9px] font-bold min-w-[16px] h-4 px-1 flex items-center justify-center"
                >{unread}</span>
              )}
            </NavLink>
            <NavLink
              to="/profile"
              data-testid="nav-profile"
              className={({ isActive }) =>
                `p-2 border transition-all ${
                  isActive ? "border-[#FF3B30] text-[#FF3B30]" : "border-[#2A2A35] hover:border-white text-white"
                }`
              }
              aria-label="Profilo"
              title="Profilo"
            >
              <User className="w-4 h-4" />
            </NavLink>
            <div className="hidden sm:block text-right">
              <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{isAdmin ? "Coach" : "Atleta"}</div>
              <div className="text-sm font-bold" data-testid="user-name">{user?.name}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 border border-[#2A2A35] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-all duration-200"
              aria-label="Esci"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* mobile nav */}
        <div className="md:hidden border-t border-[#2A2A35] overflow-x-auto">
          <div className="flex items-center gap-1 px-4 py-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`m-${n.testid}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                    isActive ? "text-[#FF3B30]" : "text-[#A0A0AB]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
      <main className="pt-20 md:pt-16 pb-16">{children}</main>
    </div>
  );
}
